import { FC, useEffect, useMemo, useState } from 'react'
import { Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useAsync, useAsyncRetry } from 'react-use'
import cn from 'classnames'
import Modal from '@common/Modal'
import Button from '@UI/New/Button'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import Select from '../base/Select/Select'
import { IcoRemove } from '@/utils/icons'
import { crmApi, crmDealApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useCRMContext } from '../../CRMContext'
import { getLeadFieldValue, type LeadFieldsSource } from '../../utils/leadFields'
import { selectProfile } from '@store/profile.reducer'
import { useSelector } from 'react-redux'
import { useLeadRefetch } from '../LeadProfileModal/LeadRefetchContext'
import mclasses from '../../styles/modal.module.css'
import classes from './index.module.css'

const ALREADY_ADDED_DEAL_TOOLTIP = 'Сделка уже добавлена в эту воронку'

type AddDealLead = LeadFieldsSource & { id: number }

export type AddDealToLeadSuccessPayload = {
  leadFunnelId: number
  funnelId: number
  managerId: number | null
}

interface AddDealToLeadModalProps {
  lead?: AddDealLead
  open: boolean
  onClose: () => void
  onSuccess?: (payload: AddDealToLeadSuccessPayload) => Promise<void> | void
}

const AddDealToLeadModal: FC<AddDealToLeadModalProps> = ({
  lead,
  open,
  onClose,
  onSuccess,
}) => {
  const addAlert = useAddAlert()
  const profile = useSelector(selectProfile)
  const currentRole = profile?.role
  const { selectedFunnelId, leadCardSnapshot } = useLeadRefetch()
  const {
    preloadingData: { funnels },
  } = useCRMContext()

  const funnelsState = useAsyncRetry(async () => {
    if (!open) return null
    return await crmApi.funnels.getAllFunnels()
  }, [open])

  const dealsState = useAsyncRetry(async () => {
    if (!open) return null
    return await crmDealApi.getAll()
  }, [open])

  const managersState = useAsync(crmApi.getAllManagers, [])
  const managers = managersState.value?.data?.data || []
  const actualFunnels = funnelsState.value
    ? funnelsState.value?.data?.data?.funnels ?? []
    : funnels
  const dealCatalog = dealsState.value?.data?.data?.deals ?? []

  const [funnelId, setFunnelId] = useState<number | null>(null)
  const [dealId, setDealId] = useState<number | null>(null)
  const [managerId, setManagerId] = useState<number | null>(null)
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setDealId(null)
    setManagerId(null)
    setComment('')
    const current =
      selectedFunnelId != null &&
      Number.isFinite(Number(selectedFunnelId)) &&
      Number(selectedFunnelId) > 0
        ? Number(selectedFunnelId)
        : null
    setFunnelId(current)
  }, [open, selectedFunnelId])

  useEffect(() => {
    if (!open || funnelId == null) return
    const exists = actualFunnels.some(
      (funnel) => Number(funnel.id) === Number(funnelId),
    )
    if (!exists && actualFunnels.length > 0) {
      setFunnelId(null)
    }
  }, [open, funnelId, actualFunnels])

  useEffect(() => {
    if (open && !lead) {
      onClose()
    }
  }, [open, lead, onClose])

  useEffect(() => {
    if (!managersState.error) return
    addAlert({
      title: 'Ошибка',
      text: 'Не удалось загрузить список менеджеров',
      type: 'error',
    })
  }, [managersState.error, addAlert])

  useEffect(() => {
    if (!open || !funnelsState.error) return
    addAlert({
      title: 'Ошибка',
      text: 'Не удалось загрузить список воронок',
      type: 'error',
    })
  }, [addAlert, funnelsState.error, open])

  useEffect(() => {
    if (!open || !dealsState.error) return
    addAlert({
      title: 'Ошибка',
      text: 'Не удалось загрузить каталог сделок',
      type: 'error',
    })
  }, [addAlert, dealsState.error, open])

  const leadName = useMemo(
    () => (lead ? getLeadFieldValue(lead, 'NAME') || '(без фио)' : '-'),
    [lead],
  )

  const funnelOptions = useMemo(
    () =>
      actualFunnels.map((funnel) => ({
        value: funnel.id,
        label: funnel.name,
      })),
    [actualFunnels],
  )

  const addedDealIdsForFunnel = useMemo(() => {
    const list = leadCardSnapshot?.current_funnels
    if (!Array.isArray(list) || funnelId == null) return new Set<number>()

    const ids = new Set<number>()
    for (const row of list) {
      if (Number(row.funnel_id) !== Number(funnelId)) continue
      const id = Number(row.deal?.id)
      if (Number.isFinite(id) && id > 0) ids.add(id)
    }
    return ids
  }, [funnelId, leadCardSnapshot])

  const dealOptions = useMemo(
    () =>
      dealCatalog.map((deal) => {
        const isAlreadyAdded = addedDealIdsForFunnel.has(Number(deal.id))
        return {
          value: deal.id,
          title: deal.name,
          disabled: isAlreadyAdded,
          label: isAlreadyAdded ? (
            <TooltipWrapper title={ALREADY_ADDED_DEAL_TOOLTIP}>
              <span style={{ display: 'block', pointerEvents: 'auto' }}>
                {deal.name}
              </span>
            </TooltipWrapper>
          ) : (
            deal.name
          ),
        }
      }),
    [addedDealIdsForFunnel, dealCatalog],
  )

  useEffect(() => {
    if (dealId == null) return
    if (addedDealIdsForFunnel.has(dealId)) {
      setDealId(null)
    }
  }, [addedDealIdsForFunnel, dealId])

  const managerOptions = useMemo(() => {
    const withRole = managers.filter((manager: any) => !!manager?.user?.role)
    const salesManagers =
      withRole.length > 0
        ? withRole.filter(
            (manager: any) => manager?.user?.role === 'sales-manager',
          )
        : managers

    return salesManagers.map((manager: any) => ({
      value: Number(manager.id),
      label: manager.fio,
    }))
  }, [managers])

  const currentManagerOption = useMemo(() => {
    const profileUserId = Number(profile?.id)
    if (!Number.isFinite(profileUserId)) return null

    return (
      managerOptions.find((option) => {
        const manager = managers.find(
          (item: any) => Number(item.id) === option.value,
        )
        const managerUserId = Number(manager?.user?.id)
        return Number.isFinite(managerUserId) && managerUserId === profileUserId
      }) ?? null
    )
  }, [managerOptions, managers, profile?.id])

  const isSalesManager = currentRole === 'sales-manager'

  useEffect(() => {
    if (!open || !isSalesManager) return
    setManagerId(currentManagerOption?.value ?? null)
  }, [open, isSalesManager, currentManagerOption?.value])

  const handleConfirm = async () => {
    if (!lead?.id) {
      addAlert({
        title: 'Ошибка',
        text: 'Лид не выбран',
        type: 'error',
      })
      return
    }
    if (!funnelId) {
      addAlert({
        title: 'Ошибка',
        text: 'Выберите воронку',
        type: 'error',
      })
      return
    }

    try {
      setSubmitting(true)
      const response = await crmApi.leads.addFunnelToLead({
        lead_id: lead.id,
        funnel_id: funnelId,
        manager_id: managerId ?? null,
        ...(dealId != null ? { deal_id: dealId } : {}),
        ...(comment.trim() !== '' ? { comment: comment.trim() } : {}),
      })
      if (isError(response)) throw new Error()

      const created = response.data?.data?.lead_funnel
      const leadFunnelId = Number(created?.id)
      if (!Number.isFinite(leadFunnelId) || leadFunnelId <= 0) {
        throw new Error('Missing lead_funnel id')
      }

      addAlert({
        title: 'Успешно',
        text: 'Сделка добавлена',
        type: 'success',
      })

      if (onSuccess) {
        await onSuccess({
          leadFunnelId,
          funnelId: Number(created?.funnel_id) || funnelId,
          managerId: managerId ?? null,
        })
      }
    } catch (error: any) {
      const backendError =
        error?.response?.data?.error ?? error?.response?.data?.message
      const errorText = Array.isArray(backendError)
        ? backendError.join('\n')
        : typeof backendError === 'string'
          ? backendError
          : 'Не удалось добавить сделку'
      addAlert({
        title: 'Ошибка',
        text: errorText,
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      active={open}
      className={cn(mclasses.modalWrapper, classes.modalWrapper) as any}
      setActive={() => {
        if (submitting) return
        onClose()
      }}
    >
      <div className={cn(mclasses.modalContainer, classes.modalContainer)}>
        <div className={mclasses.modalHeader}>
          <div className={mclasses.modalTitleContainer}>
            <span className={mclasses.modalHeaderTitle}>
              Добавить сделку лиду
            </span>
          </div>
          <div
            className={mclasses.closeIcon}
            onClick={() => {
              if (submitting) return
              onClose()
            }}
          >
            <IcoRemove />
          </div>
        </div>

        <div className={cn(mclasses.formWrapper, classes.formWrapper)}>
          <div>
            <p className={mclasses.label}>Выбранный лид</p>
            <div className={classes.leadsList}>
              <p className={classes.leadItem}>{leadName}</p>
            </div>
          </div>

          <div>
            <p className={mclasses.label}>Выберите воронку</p>
            <Select
              showSearch
              placeholder="Выберите воронку"
              value={funnelId ?? undefined}
              onChange={(value) => setFunnelId(Number(value))}
              options={funnelOptions}
              disabled={submitting}
              loading={funnelsState.loading}
            />
          </div>

          <div>
            <p className={mclasses.label}>Название сделки (необязательно)</p>
            <Select
              showSearch
              allowClear
              placeholder="Из каталога сделок"
              value={dealId ?? undefined}
              onChange={(value) =>
                setDealId(
                  value === undefined || value === null || value === ''
                    ? null
                    : Number(value),
                )
              }
              options={dealOptions}
              disabled={submitting}
              loading={dealsState.loading}
            />
          </div>

          <div>
            <p className={mclasses.label}>Комментарий сделки (необязательно)</p>
            <textarea
              className={classes.commentInput}
              placeholder="Заметка по новой сделке…"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              disabled={submitting}
              rows={3}
            />
          </div>

          <div>
            <div className={classes.managerLabel}>
              <p className={mclasses.label}>Выберите менеджера по продажам</p>
              <Tooltip
                placement="top"
                title="Менеджер для новой сделки в выбранной воронке"
              >
                <InfoCircleOutlined
                  className={classes.infoIcon}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              </Tooltip>
            </div>

            <Select
              showSearch
              allowClear={!isSalesManager}
              placeholder="Выберите менеджера"
              value={managerId ?? undefined}
              onChange={(value) =>
                setManagerId(
                  value === undefined || value === null || value === ''
                    ? null
                    : Number(value),
                )
              }
              options={managerOptions}
              loading={managersState.loading}
              disabled={submitting || isSalesManager}
            />
          </div>
        </div>

        <div className={mclasses.actions}>
          <Button color="gray" onClick={onClose} disabled={submitting}>
            Отменить
          </Button>
          <Button
            color="primary"
            onClick={handleConfirm}
            disabled={!funnelId || submitting}
            isLoading={submitting}
          >
            Добавить
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default AddDealToLeadModal
